import type { QuoteList } from '@gta/api-client'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { useCallback } from 'react'

import { PaginationBar } from '@/components/console/pagination-bar'
import { ErrorState } from '@/components/console/states'
import { PageContainer } from '@/components/layout/page-container'
import { apiErrorMessage, useQuotesPage } from '@/features/quotes/api'
import { QuotesFilterBar } from '@/features/quotes/components/quotes-filter-bar'
import { QuotesTable } from '@/features/quotes/components/quotes-table'
import {
  QUOTES_PAGE_SIZE,
  QUOTES_SEARCH_DEFAULTS,
  applyQuotesSearchPatch,
  hasQuotesFilter,
  quotesSearchSchema,
  type QuotesSearch,
} from '@/features/quotes/search-params'

/** 稳定引用:没有数据时别每次 render 都造新数组。 */
const NO_ROWS: QuoteList[] = []

export const Route = createFileRoute('/_authed/quotes/')({
  // 筛选 / 分页全部进 URL:冷加载 /quotes?status=locked&page=2 能完整还原。
  // 等于默认值的参数由 stripSearchParams 摘掉(A4 全站统一),裸 /quotes 就是干净首屏。
  validateSearch: quotesSearchSchema,
  search: {
    middlewares: [stripSearchParams(QUOTES_SEARCH_DEFAULTS)],
  },
  component: QuotesListPage,
})

function QuotesListPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = useQuotesPage(search)

  const patchSearch = useCallback(
    (patch: Partial<QuotesSearch>) => {
      void navigate({ search: (prev) => applyQuotesSearchPatch(prev, patch), replace: true })
    },
    [navigate],
  )

  const resetSearch = useCallback(() => {
    void navigate({ search: {}, replace: true })
  }, [navigate])

  const changePage = useCallback(
    (page: number) => {
      void navigate({ search: (prev) => applyQuotesSearchPatch(prev, { page }) })
    },
    [navigate],
  )

  const page = search.page
  const filtered = hasQuotesFilter(search)
  const total = query.data?.count ?? 0
  const rows = query.data?.results ?? NO_ROWS

  return (
    <PageContainer
      title="报价管理"
      breadcrumbs={[{ label: '报价管理' }]}
      description="按报价状态、来源、关键词与创建日期区间检索报价;筛选与分页状态全部保存在 URL。"
    >
      <div className="flex flex-col gap-3">
        <QuotesFilterBar value={search} onPatch={patchSearch} onReset={resetSearch} />

        {query.isError ? (
          <ErrorState
            title="报价列表加载失败"
            message={apiErrorMessage(query.error)}
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : (
          <div className="console-card overflow-hidden">
            <QuotesTable
              data={rows}
              isLoading={query.isLoading}
              isRefreshing={query.isFetching && !query.isLoading}
              emptyTitle={filtered ? '没有符合筛选条件的报价' : '暂无报价'}
              emptyDescription={filtered ? '试试放宽状态或日期区间,或清空关键词。' : undefined}
            />
            <PaginationBar page={page} pageSize={QUOTES_PAGE_SIZE} total={total} onPageChange={changePage} />
          </div>
        )}
      </div>
    </PageContainer>
  )
}
